import { Link, useRouterState } from '@tanstack/react-router'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@workspace/ui/components/collapsible'
import { Tooltip, TooltipContent, TooltipTrigger } from '@workspace/ui/components/tooltip'
import { cn } from '@workspace/ui/lib/utils'
import { ChevronRight, PanelLeftClose, PanelLeftOpen, Sparkles } from 'lucide-react'
import type { ComponentProps, ReactNode } from 'react'
import { useState } from 'react'

/**
 * A nav badge shows runway or news, never backlog: something the founder can
 * clear with a bounded action, which returns only when the condition returns.
 *
 * `text` omitted renders a bare dot — used where the point is "this needs
 * attention" without a number worth reading (campaign status, and the parent
 * group inheriting its worst child).
 */
export interface NavBadgeValue {
  text?: string
  tone: 'primary' | 'emerald' | 'amber' | 'red'
  /**
   * Hover text naming what the badge is for. A bare dot says "something here"
   * and nothing else, so anything that cannot be read from the row itself
   * belongs here — it is also what a screen reader announces.
   */
  title?: string
}

/** A second-level entry. Rendered as an indented label with no icon of its own. */
export interface NavLeaf {
  label: string
  to?: string
  /** Search params applied when navigating (e.g. the outreach channel tab) */
  search?: Record<string, string>
  badge?: NavBadgeValue
  disabled?: boolean
  /** Tooltip shown when disabled. Falls back to the label. */
  disabledTooltip?: string
}

export interface NavItem {
  icon: React.ElementType
  label: string
  /** Destination. Omitted on groups, which only expand. */
  to?: string
  /** Search params applied when navigating */
  search?: Record<string, string>
  badge?: NavBadgeValue
  disabled?: boolean
  /** Tooltip shown when disabled. Falls back to the label. */
  disabledTooltip?: string
  /** Sub-items. An item with children is a toggle, never a destination. */
  children?: NavLeaf[]
}

export interface NavSection {
  title: string
  items: NavItem[]
}

export interface AppSidebarProps {
  /** Flat nav items (compact sidebar, no sections) */
  navItems?: NavItem[]
  /** Grouped nav items (expanded sidebar with section headers) */
  navSections?: NavSection[]
  activeNav: string
  /** Whether the sidebar is in compact (icon-only) mode */
  collapsed: boolean
  /** Callback to toggle collapsed state. If not provided, sidebar is fixed. */
  onToggle?: () => void
  /** Optional content to render below the brand area (e.g. startup switcher in compact mode) */
  headerSlot?: ReactNode
  /** Item pinned to the bottom of the column, above the collapse toggle (e.g. Settings) */
  footerItem?: NavItem
  /**
   * Free-form content above the pinned footer item — a primary action the app
   * wants on every page. Kept a ReactNode so routing, dialogs and mutations
   * stay in the app; this package only owns where it sits.
   */
  footerSlot?: ReactNode
  /** URL to the brand logo image. Falls back to Sparkles icon if not provided. */
  logoSrc?: string
  /**
   * Where the brand goes when clicked. Omit to leave it inert — a logo that
   * looks like a link and isn't is worse than one that plainly isn't.
   */
  homeTo?: string
  /** Called after a nav item is clicked (e.g. to close mobile drawer) */
  onNavigate?: () => void
}

/**
 * `to` is a runtime-built string (basePath + route), so TanStack can't resolve the
 * typed search shape for it and narrows `search` to a params-reducer. Each target
 * route validates its own params via `validateSearch`, so the plain object is safe.
 */
type LinkSearch = ComponentProps<typeof Link>['search']
const asLinkSearch = (search?: Record<string, string>) => search as unknown as LinkSearch

/**
 * Which groups the founder has explicitly opened or closed, by label. The sidebar
 * remounts on every navigation, so without this the sections would spring back to
 * their default on each page change. Only labels the founder has actually toggled
 * appear here — everything else falls back to "open if it owns the current page".
 */
const OPEN_GROUPS_STORAGE_KEY = 'sidebar:open-groups'

function readOpenGroups(): Record<string, boolean> {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.localStorage.getItem(OPEN_GROUPS_STORAGE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch {
    // Private mode, quota, or hand-edited junk — a lost preference is harmless.
    return {}
  }
}

function writeOpenGroup(label: string, open: boolean) {
  if (typeof window === 'undefined') return
  try {
    const next = { ...readOpenGroups(), [label]: open }
    window.localStorage.setItem(OPEN_GROUPS_STORAGE_KEY, JSON.stringify(next))
  } catch {
    // Preference just won't persist this session.
  }
}

const NAV_ITEM_CLASSES =
  'flex h-10 items-center gap-2.5 whitespace-nowrap rounded-lg px-2.5 text-sm transition-colors'
// Active state is the tinted fill plus colour — no left rule.
const NAV_ITEM_ACTIVE_CLASSES = 'bg-primary/10 text-primary'
const NAV_ITEM_IDLE_CLASSES = 'text-foreground/80 hover:bg-white/[0.04] hover:text-foreground'

/**
 * Two levels of matching against the current location:
 *
 * - `isActive` is exact — path *and* every search param the leaf declares. It drives
 *   highlighting, so only the leaf the founder actually picked lights up. Params the
 *   leaf doesn't declare (e.g. `topup`) are ignored, so they never break it.
 * - `ownsPath` is path-only. It drives group expansion, so landing on `/outreach`
 *   with no `?tab=` still opens the Outreach group even though no single leaf matches.
 */
function useLeafMatchers() {
  const location = useRouterState({ select: (state) => state.location })

  const ownsPath = (item: NavLeaf) => !!item.to && item.to === location.pathname

  const isActive = (item: NavLeaf) => {
    if (!ownsPath(item)) return false
    if (!item.search) return true
    const current = location.search as Record<string, unknown>
    return Object.entries(item.search).every(([key, value]) => current[key] === value)
  }

  return { isActive, ownsPath }
}

/**
 * The logo and wordmark at the top of the column. A link when the app supplies
 * a destination, plain markup when it doesn't — the two render identically, so
 * the only difference the founder sees is whether it responds.
 */
function Brand({
  logoSrc,
  collapsed,
  homeTo,
  onNavigate,
}: {
  logoSrc?: string
  collapsed: boolean
  homeTo?: string
  onNavigate?: () => void
}) {
  const inner = (
    <>
      {logoSrc ? (
        <img src={logoSrc} alt="SeedForge" className="size-7 shrink-0" />
      ) : (
        <div className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-primary">
          <Sparkles className="size-4 text-white" />
        </div>
      )}
      <span
        className={cn(
          'truncate font-bold text-[16px] text-foreground tracking-[-0.01em] transition-opacity duration-200',
          collapsed ? 'opacity-0' : 'opacity-100'
        )}
      >
        SeedForge
      </span>
    </>
  )

  const className = 'flex h-14 items-center gap-2.5 border-b border-border px-4'

  if (!homeTo) return <div className={className}>{inner}</div>

  return (
    <Link
      to={homeTo}
      onClick={onNavigate}
      aria-label="SeedForge, go to your dashboard"
      className={cn(
        className,
        'transition-colors hover:bg-white/[0.04] focus-visible:bg-white/[0.04] focus-visible:outline-none'
      )}
    >
      {inner}
    </Link>
  )
}

export function AppSidebar({
  navItems,
  navSections,
  activeNav,
  collapsed,
  onToggle,
  headerSlot,
  footerItem,
  footerSlot,
  logoSrc,
  homeTo,
  onNavigate,
}: AppSidebarProps) {
  const { isActive: isLeafActive, ownsPath: isLeafPath } = useLeafMatchers()

  const renderItem = (item: NavItem) =>
    item.children ? (
      <SidebarNavGroup
        key={item.label}
        item={item}
        collapsed={collapsed}
        isLeafActive={isLeafActive}
        isLeafPath={isLeafPath}
        onExpandSidebar={onToggle}
        onNavigate={onNavigate}
      />
    ) : (
      <SidebarNavItem
        key={item.label}
        item={item}
        isActive={item.label === activeNav}
        collapsed={collapsed}
        onNavigate={onNavigate}
      />
    )

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-40 flex h-screen flex-col overflow-hidden border-r border-border bg-[#111111] transition-all duration-200',
        collapsed ? 'w-16' : 'w-80'
      )}
    >
      {/* Brand — the way back to the dashboard from anywhere, which is what
          everyone tries first with a logo in this corner. */}
      <Brand logoSrc={logoSrc} collapsed={collapsed} homeTo={homeTo} onNavigate={onNavigate} />

      {headerSlot}

      {/* Nav — sectioned (expanded) or flat */}
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {navSections
          ? navSections.map((section) => (
              <div key={section.title} className="mb-4">
                <p
                  className={cn(
                    'mb-1 whitespace-nowrap px-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80 transition-opacity duration-200',
                    collapsed ? 'opacity-0' : 'opacity-100'
                  )}
                >
                  {section.title}
                </p>
                <div className="flex flex-col gap-0.5">{section.items.map(renderItem)}</div>
              </div>
            ))
          : navItems && <div className="flex flex-col gap-0.5">{navItems.map(renderItem)}</div>}
      </nav>

      {/* App-supplied primary action. Hidden while collapsed: it is a labelled
          button with no icon-only form. */}
      {footerSlot && !collapsed && <div className="px-3 pb-5 pt-2">{footerSlot}</div>}

      {/* Pinned footer item (e.g. Settings) */}
      {footerItem && (
        <div className="border-t border-border px-3 py-2">
          <SidebarNavItem
            item={footerItem}
            isActive={footerItem.label === activeNav}
            collapsed={collapsed}
            onNavigate={onNavigate}
          />
        </div>
      )}

      {/* Collapse toggle */}
      {onToggle && (
        <div className="border-t border-border p-3">
          <button
            type="button"
            onClick={onToggle}
            className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-lg px-2 py-2 text-sm text-muted-foreground transition-colors hover:bg-white/[0.04] hover:text-foreground"
          >
            {collapsed ? (
              <PanelLeftOpen className="size-4 shrink-0" />
            ) : (
              <>
                <PanelLeftClose className="size-4 shrink-0" />
                <span
                  className={cn(
                    'transition-opacity duration-200',
                    collapsed ? 'opacity-0' : 'opacity-100'
                  )}
                >
                  Collapse
                </span>
              </>
            )}
          </button>
        </div>
      )}
    </aside>
  )
}

const BADGE_TONE_CLASSES: Record<NavBadgeValue['tone'], string> = {
  primary: 'bg-primary text-white',
  emerald: 'bg-emerald-500 text-white',
  amber: 'bg-amber-500 text-white',
  red: 'bg-red-500 text-white',
}

const DOT_TONE_CLASSES: Record<NavBadgeValue['tone'], string> = {
  primary: 'bg-primary',
  emerald: 'bg-emerald-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
}

/**
 * Badge pill. The width grows with the content rather than being a fixed circle,
 * so counts and short phrases ("0 ready", "4 days left") both fit.
 */
function NavBadge({ badge, className }: { badge: NavBadgeValue; className?: string }) {
  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full font-bold tabular-nums',
        BADGE_TONE_CLASSES[badge.tone],
        className
      )}
    >
      {badge.text}
    </span>
  )
}

/** Attention marker with nothing worth reading — a status, not a count. */
function NavDot({ badge, className }: { badge: NavBadgeValue; className?: string }) {
  const dotClass = cn('size-2 shrink-0 rounded-full', DOT_TONE_CLASSES[badge.tone], className)

  // An untitled dot is decoration on a row that already reads, so it stays out
  // of the accessibility tree entirely.
  if (!badge.title) return <span className={dotClass} aria-hidden="true" />

  // A titled one carries the only description of why it is there: announced as
  // a graphic, and shown on hover for everyone else.
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={dotClass} role="img" aria-label={badge.title} />
      </TooltipTrigger>
      <TooltipContent side="right" sideOffset={8}>
        {badge.title}
      </TooltipContent>
    </Tooltip>
  )
}

function NavItemContents({
  item,
  collapsed,
  trailing,
}: {
  item: NavItem
  collapsed: boolean
  trailing?: ReactNode
}) {
  const badge = item.badge

  return (
    <>
      <span className="relative shrink-0">
        <item.icon className="size-4" />
        {/* Icon-only rail has no room for text, so any badge collapses to a dot. */}
        {badge && collapsed && <NavDot badge={badge} className="-right-1 -top-0.5 absolute" />}
      </span>
      <span
        className={cn(
          'min-w-0 flex-1 truncate text-left transition-opacity duration-200',
          collapsed ? 'opacity-0' : 'opacity-100'
        )}
      >
        {item.label}
      </span>
      {badge &&
        !collapsed &&
        (badge.text ? (
          <NavBadge badge={badge} className="h-5 min-w-5 px-1.5 text-[10px]" />
        ) : (
          <NavDot badge={badge} />
        ))}
      {!collapsed && trailing}
    </>
  )
}

/**
 * A collapsible group. The trigger only opens and closes the section — it never navigates.
 * While the sidebar is icon-only there is no room for children, so clicking expands the
 * sidebar first and opens the group with it.
 */
function SidebarNavGroup({
  item,
  collapsed,
  isLeafActive,
  isLeafPath,
  onExpandSidebar,
  onNavigate,
}: {
  item: NavItem
  collapsed: boolean
  isLeafActive: (item: NavLeaf) => boolean
  isLeafPath: (item: NavLeaf) => boolean
  onExpandSidebar?: () => void
  onNavigate?: () => void
}) {
  const children = item.children ?? []
  const isDisabled = !!item.disabled
  // Path-only, so the group opens on any of its pages — including ones reached
  // without the search params a specific leaf carries.
  const ownsCurrentPage = !isDisabled && children.some(isLeafPath)
  // Undefined until the founder touches it, so an untouched group still opens for
  // the page it owns. Once set, the stored choice wins on every page.
  const [manuallyOpen, setManuallyOpen] = useState<boolean | undefined>(
    () => readOpenGroups()[item.label]
  )
  // A gated section has nothing worth showing inside it, so it stays shut until
  // the gate lifts — the tooltip explains why rather than opening onto dead links.
  const open = !collapsed && !isDisabled && (manuallyOpen ?? ownsCurrentPage)

  const setOpen = (next: boolean) => {
    setManuallyOpen(next)
    writeOpenGroup(item.label, next)
  }

  const handleOpenChange = (next: boolean) => {
    if (collapsed) {
      onExpandSidebar?.()
      setOpen(true)
      return
    }
    setOpen(next)
  }

  const contents = (
    <NavItemContents
      item={item}
      collapsed={collapsed}
      trailing={
        !isDisabled && (
          <ChevronRight
            className={cn('size-3.5 shrink-0 transition-transform', open && 'rotate-90')}
          />
        )
      }
    />
  )

  // A disabled group renders as a static row rather than a disabled <button>:
  // a disabled button swallows hover, which would suppress the tooltip that
  // explains why the section is locked. Same treatment as a disabled leaf.
  if (isDisabled) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            aria-disabled="true"
            className={cn(NAV_ITEM_CLASSES, 'w-full cursor-not-allowed text-foreground/40')}
          >
            {contents}
          </div>
        </TooltipTrigger>
        <TooltipContent side="right" sideOffset={8}>
          {item.disabledTooltip ?? item.label}
        </TooltipContent>
      </Tooltip>
    )
  }

  const trigger = (
    <CollapsibleTrigger
      className={cn(
        NAV_ITEM_CLASSES,
        'w-full',
        // The group carries the active treatment for the whole section, open or
        // closed — otherwise nothing at the top level is marked while browsing a
        // section, and Home ends up looking like the only highlighted item.
        ownsCurrentPage ? NAV_ITEM_ACTIVE_CLASSES : NAV_ITEM_IDLE_CLASSES
      )}
    >
      {contents}
    </CollapsibleTrigger>
  )

  return (
    <Collapsible open={open} onOpenChange={handleOpenChange}>
      {collapsed ? (
        <Tooltip>
          <TooltipTrigger asChild>{trigger}</TooltipTrigger>
          <TooltipContent side="right" sideOffset={8}>
            {item.label}
          </TooltipContent>
        </Tooltip>
      ) : (
        trigger
      )}
      <CollapsibleContent>
        <div className="mt-0.5 flex flex-col gap-0.5">
          {children.map((child) => (
            <SidebarNavLeaf
              key={child.label}
              item={child}
              isActive={isLeafActive(child)}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

function SidebarNavLeaf({
  item,
  isActive,
  onNavigate,
}: {
  item: NavLeaf
  isActive: boolean
  onNavigate?: () => void
}) {
  const baseClasses =
    'flex items-center gap-2 whitespace-nowrap rounded-lg py-2 pl-10 pr-2.5 text-sm transition-colors'

  if (item.disabled) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            aria-disabled="true"
            className={cn(baseClasses, 'cursor-not-allowed text-foreground/40')}
          >
            {item.label}
          </div>
        </TooltipTrigger>
        <TooltipContent side="right" sideOffset={8}>
          {item.disabledTooltip ?? item.label}
        </TooltipContent>
      </Tooltip>
    )
  }

  return (
    <Link
      to={item.to}
      search={asLinkSearch(item.search)}
      onClick={onNavigate}
      className={cn(
        baseClasses,
        isActive ? 'text-primary' : 'text-foreground/70 hover:bg-white/[0.04] hover:text-foreground'
      )}
    >
      <span className="min-w-0 flex-1 truncate">{item.label}</span>
      {item.badge &&
        (item.badge.text ? (
          <NavBadge badge={item.badge} className="h-4 min-w-4 px-1.5 text-[10px]" />
        ) : (
          <NavDot badge={item.badge} />
        ))}
    </Link>
  )
}

function SidebarNavItem({
  item,
  isActive,
  collapsed,
  onNavigate,
}: {
  item: NavItem
  isActive: boolean
  collapsed: boolean
  onNavigate?: () => void
}) {
  const isDisabled = !!item.disabled
  const contents = <NavItemContents item={item} collapsed={collapsed} />

  const trigger = isDisabled ? (
    <div
      aria-disabled="true"
      className={cn(NAV_ITEM_CLASSES, 'cursor-not-allowed text-foreground/40')}
    >
      {contents}
    </div>
  ) : (
    <Link
      to={item.to}
      search={asLinkSearch(item.search)}
      onClick={onNavigate}
      className={cn(NAV_ITEM_CLASSES, isActive ? NAV_ITEM_ACTIVE_CLASSES : NAV_ITEM_IDLE_CLASSES)}
    >
      {contents}
    </Link>
  )

  const tooltipLabel = isDisabled ? (item.disabledTooltip ?? item.label) : item.label
  const showTooltip = collapsed || isDisabled

  if (showTooltip) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent side="right" sideOffset={8}>
          {tooltipLabel}
        </TooltipContent>
      </Tooltip>
    )
  }

  return trigger
}
